/**
 * HelpCenter — Phase 2 knowledge-base scaffold.
 *
 * Single-page methodology reference with anchor-linked TOC. Covers the
 * core concepts a coordinator or PM needs to interpret their dashboard:
 * Cyber Risk Signal, Big 6 Threat Vectors, 360 family, Goldilocks Zones,
 * Outside Pressures, Cyber Poverty Line, and Recommendations / Iris.
 *
 * Phase 2 v1: ships substantive content for the most-asked sections and
 * marks the remaining ones as Coming soon so users can see what is
 * planned. Add content over time without changing the IA.
 */
import React from 'react';
import { BookOpen, ArrowRight } from 'lucide-react';

const SECTIONS = [
  { id: 'crs',           title: 'Cyber Risk Signal (CRS)',  status: 'ready' },
  { id: 'big-six',       title: 'Big 6 Threat Vectors',     status: 'ready' },
  { id: 'three-sixty',   title: '360 Family',                status: 'ready' },
  { id: 'goldilocks',    title: 'Goldilocks Zones',          status: 'ready' },
  { id: 'outside',       title: 'Outside Pressures',         status: 'ready' },
  { id: 'poverty',       title: 'Cyber Poverty Line',        status: 'ready' },
  // Hidden 2026-05-18 per Pat — Recommendations & Iris section not finalized;
  // restore when copy is ready. See the matching commented <Section id="recs">
  // block below — uncomment BOTH to bring it back.
  // { id: 'recs',          title: 'Recommendations & Iris',    status: 'ready' },
];

const PAGE_BG   = '#0a0e1a';
const PANEL_BG  = 'rgba(255,255,255,0.03)';
const PANEL_BD  = 'rgba(255,255,255,0.08)';
const TEXT      = '#e6f1ff';
const TEXT_DIM  = '#a8b2cc';
const TEXT_MUTE = '#7a8599';
const ACCENT    = '#60a5fa';
const ACCENT_BG = 'rgba(96,165,250,0.10)';

function Section({ id, title, children, status }) {
  return (
    <section
      id={id}
      style={{
        background: PANEL_BG,
        border: `1px solid ${PANEL_BD}`,
        borderRadius: 12,
        padding: '24px 28px',
        marginBottom: 20,
        scrollMarginTop: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <h2 style={{
          color: TEXT,
          fontSize: '1.35rem',
          fontWeight: 700,
          margin: 0,
        }}>
          {title}
        </h2>
        {status === 'placeholder' && (
          <span style={{
            color: TEXT_MUTE,
            fontSize: '0.75rem',
            padding: '2px 8px',
            borderRadius: 4,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            Coming soon
          </span>
        )}
      </div>
      <div style={{ color: TEXT_DIM, fontSize: '0.92rem', lineHeight: 1.65 }}>
        {children}
      </div>
    </section>
  );
}

function Definition({ term, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <strong style={{ color: TEXT }}>{term}</strong>
      <span style={{ color: TEXT_DIM }}> — {children}</span>
    </div>
  );
}

export default function HelpCenter() {
  return (
    <div style={{
      minHeight: '100vh',
      background: PAGE_BG,
      padding: '32px 24px 80px',
      fontFamily: 'Arial, sans-serif',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <BookOpen size={24} color={ACCENT} aria-hidden="true" />
          <h1 style={{ color: TEXT, fontSize: '1.75rem', fontWeight: 700, margin: 0 }}>
            Foundations Help Center
          </h1>
        </div>
        <p style={{ color: TEXT_MUTE, fontSize: '0.95rem', margin: '0 0 32px', maxWidth: 720 }}>
          Methodology reference for interpreting your Foundations assessment.
          Explains how each score is computed, what it measures, and what to
          do with it. For module-specific orientation, expand the
          <em style={{ color: TEXT_DIM }}> How It Works</em> panel at the
          top of each module page.
        </p>

        {/* TOC */}
        <div style={{
          background: ACCENT_BG,
          border: `1px solid ${PANEL_BD}`,
          borderRadius: 10,
          padding: '16px 20px',
          marginBottom: 28,
        }}>
          <div style={{ color: ACCENT, fontSize: '0.85rem', fontWeight: 600, marginBottom: 10 }}>
            On this page
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 8,
          }}>
            {SECTIONS.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                style={{
                  color: TEXT_DIM,
                  fontSize: '0.88rem',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 0',
                }}
              >
                <ArrowRight size={12} color={ACCENT} aria-hidden="true" />
                {s.title}
                {s.status === 'placeholder' && (
                  <span style={{ color: TEXT_MUTE, fontSize: '0.7rem' }}>(soon)</span>
                )}
              </a>
            ))}
          </div>
        </div>

        {/* Content */}
        <Section id="crs" title="Cyber Risk Signal (CRS)" status="ready">
          <p>
            The Cyber Risk Signal is the headline 0&ndash;100 score on every
            Foundations dashboard. It compresses the entire assessment
            &mdash; every persona&apos;s answers, the Big 6 category scores,
            outside-pressures signals, and weighting factors &mdash; into a
            single number you can track over time.
          </p>
          <p>
            CRS is computed deterministically (no AI). Each persona&apos;s
            answers are weighted by their authority for the question
            (the CEO&apos;s answer to a governance question carries more
            weight than an Operations persona&apos;s answer to the same
            question). The category-level scores roll up into the CRS via
            an industry-calibrated weighting that reflects which Big 6
            categories matter most for your sector.
          </p>
          <p style={{ color: TEXT_MUTE, fontSize: '0.85rem' }}>
            <strong style={{ color: TEXT_DIM }}>How to use it:</strong>{' '}
            Track CRS movement across re-assessments. A 5-point drop
            between quarters is more meaningful than the absolute number.
            Industry benchmarks on the dashboard show where you sit
            relative to comparable organizations.
          </p>
        </Section>

        <Section id="big-six" title="Big 6 Threat Vectors" status="ready">
          <p>
            Six risk domains scored independently, each on a 0&ndash;100
            scale. They feed CRS but are also useful standalone:
            drilling into a low-scoring vector tells you exactly which
            questions drove the score and which persona disagreed with
            which.
          </p>
          <div style={{ marginTop: 14 }}>
            <Definition term="Governance">
              Policy maturity, accountability structures, board engagement,
              regulatory alignment. Where the formal authority for
              security decisions lives.
            </Definition>
            <Definition term="Technology">
              Tooling coverage, patch discipline, architecture posture,
              cloud configuration. Your technical defenses as built.
            </Definition>
            <Definition term="Data">
              Classification, encryption, access controls, retention
              practices. Where your sensitive data lives and who can
              touch it.
            </Definition>
            <Definition term="People">
              Training, awareness, role-based controls, insider-risk
              posture. The human surface area.
            </Definition>
            <Definition term="Operations">
              Incident response, monitoring, backup discipline,
              business continuity. How you actually run security day to day.
            </Definition>
            <Definition term="Third-Party">
              Supplier risk, contract terms, supply-chain dependencies,
              vendor security reviews. Risk you take on through others.
            </Definition>
          </div>
        </Section>

        <Section id="three-sixty" title="360 Family" status="ready">
          <p>
            The 360 family decomposes your overall posture along four
            different cuts so you can see where the risk concentrates.
            All four are scored 0&ndash;100 from the same underlying
            persona responses, just aggregated differently.
          </p>
          <div style={{ marginTop: 14 }}>
            <Definition term="Cyber360 (C360)">
              Pure cybersecurity posture &mdash; technology, operations,
              third-party. The most defensible operational measure.
            </Definition>
            <Definition term="Data360 (D360)">
              Data-centric view &mdash; classification, residency,
              access, regulated-data handling. Maps closely to compliance
              regimes (HIPAA, GDPR, CMMC).
            </Definition>
            <Definition term="People360 (P360)">
              Human-factor view &mdash; training, role clarity,
              awareness, insider risk. Pairs with the People&nbsp;Big&nbsp;6
              vector for cause-vs-effect analysis.
            </Definition>
            <Definition term="E360 (Institutional / Technical Debt)">
              Two debt scores: <em>institutional</em> debt
              (unresolved policy / process gaps) and <em>technical</em>
              debt (legacy systems, deferred upgrades). Higher = more
              future remediation cost baked in.
            </Definition>
          </div>
        </Section>

        <Section id="goldilocks" title="Goldilocks Zones" status="ready">
          <p>
            The Goldilocks model locates your investment on a spectrum
            for each readiness dimension: <em>under-invested</em>,
            <em> right-sized</em>, or <em>over-invested</em>. Calibrated
            against industry-specific benchmarks for your size, the
            zones tell you not just whether you spend enough on security,
            but where additional dollars produce diminishing returns.
          </p>
          <p>
            Above the zone is not always wrong &mdash; some organizations
            have legitimate reasons to over-invest in one dimension (e.g.,
            heavy regulatory exposure, recent incident). The point is to
            surface where you can <em>rebalance</em> rather than just
            spend more.
          </p>
          <p style={{ color: TEXT_MUTE, fontSize: '0.85rem' }}>
            <strong style={{ color: TEXT_DIM }}>Read with care:</strong>{' '}
            The Goldilocks scoring is not a linear inversion of a
            friction score. Locating on the spectrum requires both your
            spend signal and your coverage data &mdash; the model needs
            both inputs to place you accurately.
          </p>
        </Section>

        <Section id="outside" title="Outside Pressures" status="ready">
          <p>
            Strategic signals from outside your organization that bias
            your risk profile: geopolitical shifts, regulatory changes,
            supply-chain disruption, market and economic conditions,
            public-perception dynamics. These move independently of your
            internal posture &mdash; you can have a perfect internal
            assessment and still see your risk profile shift because a
            sanctions regime changed or a trade relationship deteriorated.
          </p>
          <p>
            <strong style={{ color: TEXT }}>Important:</strong> Outside
            Pressures is a <em>strategic</em> indicator. It is{' '}
            <em>not</em> a feed of technical vulnerabilities (CVEs, KEV
            entries, IOCs). Technical threat data lives on the PRISM
            side; Foundations Outside Pressures is the macro counterpart.
          </p>
        </Section>

        <Section id="poverty" title="Cyber Poverty Line" status="ready">
          <p>
            The Cyber Poverty Line is the industry-specific minimum
            effective cyber spend for an organization of your size.
            Below the line you are structurally under-resourced for the
            threats your sector faces &mdash; not because you are
            unprepared, but because the dollars to deploy adequate
            controls simply are not there. Above the line you have the
            budget headroom required, but the gauge says nothing about
            whether you have spent it well.
          </p>
          <p>
            The line is calibrated two ways. First, by <strong style={{ color: TEXT }}>sector</strong>:
            healthcare, defense, financial services, and energy face
            different threat profiles and regulatory burdens, so the
            same dollar buys different amounts of risk reduction. Second,
            by <strong style={{ color: TEXT }}>size</strong>: a 50-employee
            company has fixed-cost minima (IR retainer, baseline
            tooling, training) that scale differently than a
            5,000-employee company. The gauge accounts for both.
          </p>
          <p>
            <strong style={{ color: TEXT }}>The critical caveat:</strong>{' '}
            Poverty Line measures <em>spend level</em>, not{' '}
            <em>coverage quality</em>. A customer can sit comfortably
            above the line with all of their money in endpoint protection
            and nothing in identity or network detection &mdash; the
            gauge will look green while the actual posture is exposed.
            A green Poverty Line gauge is necessary but not sufficient.
          </p>
          <p style={{ color: TEXT_MUTE, fontSize: '0.85rem' }}>
            <strong style={{ color: TEXT_DIM }}>How to read it:</strong>{' '}
            Pair the Poverty Line with the Coverage Matrix and the
            P/D/R (Protect / Detect / Respond) maturity scores. Those
            are the signals that tell you whether the spend is{' '}
            <em>effective</em>. Use Poverty Line to answer
            &ldquo;is the budget there?&rdquo; and the coverage signals
            to answer &ldquo;is it deployed correctly?&rdquo;
          </p>
        </Section>

        {/* Hidden 2026-05-18 per Pat — Recommendations & Iris section not finalized;
            restore when copy is ready. SECTIONS array entry above is also commented.
            Uncomment BOTH to bring it back. */}
        {/*
        <Section id="recs" title="Recommendations & Iris" status="ready">
          <p>
            Foundations ships with a curated library of 1,220 cybersecurity
            recommendations spanning every Big 6 vector and maturity
            level. The RecommendationSelectorEngine scores each candidate
            against your assessment using the{' '}
            <strong style={{ color: TEXT }}>RAG Rubric formula</strong>:
          </p>
          <pre style={{
            background: 'rgba(0,0,0,0.25)',
            border: `1px solid ${PANEL_BD}`,
            borderRadius: 6,
            padding: '12px 16px',
            color: TEXT,
            fontSize: '0.85rem',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            overflowX: 'auto',
            margin: '8px 0 16px',
          }}>
            Priority = (Impact &times; |Maturity &minus; 3|) / Effort + QuickWinBoost
          </pre>
          <p>
            <strong style={{ color: TEXT }}>Impact</strong> reflects how
            much a recommendation moves the needle on a vector.{' '}
            <strong style={{ color: TEXT }}>|Maturity &minus; 3|</strong>{' '}
            weights recommendations toward the middle of the maturity
            scale (where most movement is possible);
            organizations at the extremes get fewer false positives.{' '}
            <strong style={{ color: TEXT }}>Effort</strong> divides so
            cheaper interventions rank higher all else equal.{' '}
            <strong style={{ color: TEXT }}>QuickWinBoost</strong>{' '}
            promotes high-impact / low-effort items to the top so you
            see actionable items first.
          </p>
          <p>
            The top candidates are then handed to{' '}
            <strong style={{ color: TEXT }}>Iris</strong>, our AI assistant,
            for contextualization. Iris drafts a per-recommendation
            rationale specific to your organization &mdash; it sees your
            industry, headcount, revenue, CRS, Big 6 weak spots, persona
            gaps, and Cyber Poverty Line position, and shapes the
            language accordingly. Rationales cite standards by name
            (NIST CSF, CMMC, ISO 27001 as relevant) and end with concrete
            action verbs.
          </p>
          <p>
            <strong style={{ color: TEXT }}>Iris drafts; you decide.</strong>{' '}
            Every Iris-generated rationale carries a confidence indicator
            and lands in a draft state. You review individually or use
            bulk-accept controls; rejected or edited drafts feed back
            into the system as training signal. The human stays above
            the loop on every action that reaches your action plan.
          </p>
          <p style={{ color: TEXT_MUTE, fontSize: '0.85rem' }}>
            <strong style={{ color: TEXT_DIM }}>If Iris is unavailable:</strong>{' '}
            The selector engine returns its top-scored Postgres results
            without AI synthesis (graceful degradation). You get the
            same recommendations in the same priority order &mdash;
            just without the per-organization rationale text.
          </p>
        </Section>
        */}

      </div>
    </div>
  );
}
